import React, { useEffect, useState } from 'react';
import { Platform, View, StyleSheet, Image } from 'react-native';
import { WebHeader } from './WebHeader';
import { WebPromoSection } from './WebPromoSection';
import { navigationRef } from '@/navigation/navigationRef';
import { logo } from '@/assets';

type AppShellProps = {
  children: React.ReactNode;
};

// Check URL to determine if we're on Home screen (HomeRoot)
const checkIsHomeFromURL = (): boolean => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  const pathLower = pathname.toLowerCase();
  
  // Check if pathname contains 'homeroot' as a route name
  // But exclude if it's part of another route like 'MeetingAnnouncement'
  // Note: Root path '/' is now Auth screen on web, not Home
  return (pathLower.includes('homeroot') && !pathLower.includes('meetingannouncement'));
};

// Check if current URL is an auth screen (Splash, Auth, Register steps)
const checkIsAuthScreen = (): boolean => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  const pathname = window.location.pathname.toLowerCase();
  const normalizedPath = pathname.replace(/\/$/, '') || '/';
  
  // Auth screens: /, /welcome, /auth, /register, /register/step2, /register/step3
  // Root path is also considered auth screen when user is not authenticated
  const authPaths = ['/', '/welcome', '/auth', '/register'];
  
  return authPaths.some(path => normalizedPath === path || (path !== '/' && normalizedPath.startsWith(path + '/')));
};

export const AppShell: React.FC<AppShellProps> = ({ children }) => {

  // Calculate maxWidth directly during render - check only URL
  // This ensures correct width on initial load
  let maxWidth = 1000; // Default to 1000 (most screens)
  if (Platform.OS === 'web') {
    // Check only URL - URL is the source of truth
    // Only HomeRoot gets 600px, all other screens get 1000px
    const urlIsHome = checkIsHomeFromURL();
    maxWidth = urlIsHome ? 600 : 1000;
  }

  // Track current pathname to detect changes
  const [currentPathname, setCurrentPathname] = useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return '';
  });

  // Force re-render when URL changes to update maxWidth
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const checkRoute = () => {
      // Check if pathname actually changed
      const newPathname = window.location.pathname;
      if (newPathname !== currentPathname) {
        setCurrentPathname(newPathname);
      }
    };
    
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', checkRoute);
    
    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      // Small delay to ensure URL is updated
      setTimeout(checkRoute, 0);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      // Small delay to ensure URL is updated
      setTimeout(checkRoute, 0);
    };
    
    // Also listen for navigation state changes (in case URL changes)
    const unsubscribe = navigationRef.addListener('state', checkRoute);
    
    // Poll for URL changes (fallback for cases where events don't fire)
   // const intervalId = setInterval(checkRoute, 100);

    return () => {
      window.removeEventListener('popstate', checkRoute);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
     // clearInterval(intervalId);
      unsubscribe();
    };
  }, [currentPathname]);

  // Inject global CSS to make ScrollViews not scroll internally
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const styleId = 'app-shell-styles';
    if (document.getElementById(styleId)) return;
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .phone-frame-content [style*="overflow: auto"],
      .phone-frame-content [style*="overflow: scroll"],
      .phone-frame-content [style*="overflow-y: auto"],
      .phone-frame-content [style*="overflow-y: scroll"] {
        overflow: visible !important;
        overflow-y: visible !important;
      }
      .phone-frame-wrapper {
        max-width: var(--phone-frame-max-width, 1000px) !important;
      }
      @media (max-width: 900px) {
        .promo-column {
          display: none !important;
        }
        .app-column {
          flex: 1 !important;
          max-width: 100% !important;
          justify-content: center !important;
          padding-top: 30px !important;
          height: auto !important;
        }
        .auth-main {
          justify-content: center !important;
          align-items: flex-start !important;
          padding-bottom: 0 !important;
        }
        .app-column-inner {
          max-width: 100% !important;
          height: calc(100vh - 60px - 30px - 16px - 70px) !important;
        }
        .phone-frame-wrapper {
          height: calc(100vh - 60px - 30px - 16px - 70px) !important;
          max-height: calc(100vh - 60px - 30px - 16px - 70px) !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Update CSS variable when maxWidth changes
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--phone-frame-max-width', `${maxWidth}px`);
    }
  }, [maxWidth]);

  // Web layout
  if (Platform.OS === 'web') {
    const isAuthScreen = checkIsAuthScreen();
    
    const phoneFrameStyle = {
      ...webStyles.phoneFrame,
      maxWidth: isAuthScreen ? '320px' : `${maxWidth}px`,
      minHeight: isAuthScreen ? '600px' : undefined,
      boxShadow: isAuthScreen ? '0 8px 20px rgba(0, 0, 0, 0.15)' : '0 4px 24px rgba(0, 0, 0, 0.1)',
    } as React.CSSProperties;

    // Auth screens: two-column layout with promo on left, app on right
    if (isAuthScreen) {
      return (
        <div style={webStyles.root}>
          {/* Header without navigation */}
          <WebHeader hideNavigation />
          
          {/* Two-column layout for auth screens */}
          <div className="auth-main" style={webStyles.authMain}>
            {/* Left side - promo section */}
            <div className="promo-column" style={webStyles.promoColumn}>
              <WebPromoSection />
            </div>
            
            {/* Right side - app container with logo above */}
            <div className="app-column" style={webStyles.appColumn}>
              <div className="app-column-inner" style={webStyles.appColumnInner}>
                {/* Logo above phone frame */}
                <div style={webStyles.logoContainer}>
                  <Image
                    source={logo}
                    style={{ width: 110, height: 70 }}
                    resizeMode="contain"
                  />
                </div>
                <div className="phone-frame-wrapper" style={phoneFrameStyle}>
                  <div className="phone-frame-content" style={webStyles.content}>
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Regular layout for authenticated screens
    return (
      <div style={webStyles.root}>
        {/* Header */}
        <WebHeader />
        
        {/* Phone frame - centered */}
        <div style={webStyles.main}>
          <div className="phone-frame-wrapper" style={phoneFrameStyle}>
            <div className="phone-frame-content" style={webStyles.content}>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Native: full screen
  return <View style={styles.nativeContainer}>{children}</View>;
};

const webStyles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  },
  // Auth screen two-column layout
  authMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 48px 100px 48px',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
  },
  promoColumn: {
    flex: '0 0 55%',
    maxWidth: '55%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '640px',
    paddingTop: '40px',
  },
  appColumn: {
    flex: '0 0 45%',
    maxWidth: '45%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '600px',
    paddingTop: '40px',
  },
  appColumnInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '320px',
    height: '100%',
  },
  logoContainer: {
    marginBottom: '16px',
  },
  phoneFrame: {
    width: '100%',
    maxWidth: 1000, // Default to 1000 (most screens) - will be overridden by inline style
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: 24,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 500,
    borderRadius: 16,
    overflow: 'hidden',
  },
};

const styles = StyleSheet.create({
  nativeContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
});
